/**
 * A-14 — admin reviews DPDP deletion requests: approve (→ purge) or reject.
 *
 * ADM-15: each request names who asked (and the phone's last 4 digits) before anything
 * irreversible; approving asks first, with "delete" typed to confirm; rejecting takes the reason
 * the requester is shown; and the buttons hold while a review is in flight, so a double click
 * can't send it twice.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { adminListDeletions, adminReviewDeletion } from "../../api/analytics";
import type { AdminDeletionRequest } from "../../api/analytics";
import { ApiError, describeError } from "../../api/errors";
import { ConfirmDelete } from "../../components/ui/ConfirmDelete";
import { ExportButton } from "../../components/ui/ExportButton";
import { Select } from "../../components/ui/Select";
import { phoneLabel } from "../../lib/phone";
import { sheetDateTime, sheetLabel, type ExportColumn } from "../../lib/tableExport";

const FILTERS: [string, string][] = [
  ["pending", "Pending"],
  ["approved", "Approved"],
  ["completed", "Completed"],
  ["rejected", "Rejected"],
  ["failed", "Failed"],
  ["", "All"],
];

const STATUS_TONE: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-sky-100 text-sky-800",
  completed: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
  failed: "bg-red-100 text-red-700",
};

const OUTCOME: Record<string, string> = {
  completed: "Approved; the data is purged.",
  approved: "Approved; the purge is under way.",
  rejected: "Rejected; the requester sees your reason.",
};

// The API's limit on the reason (DeletionReviewIn.note).
const NOTE_MAX = 500;

type Review = { id: string; approve: boolean; note?: string };

// The phone only ever leaves the API masked.
const EXPORT_COLUMNS: ExportColumn<AdminDeletionRequest>[] = [
  { header: "Requested (IST)", value: (d) => sheetDateTime(d.created_at) },
  { header: "Requester", value: (d) => d.requester_name },
  { header: "Phone (masked)", value: (d) => d.requester_phone_masked },
  { header: "What", value: (d) => scopeLabel(d.scope) },
  { header: "Reason", value: (d) => d.reason },
  { header: "Status", value: (d) => sheetLabel(d.status) },
  { header: "Reviewed by", value: (d) => d.reviewed_by_name },
  { header: "Reviewed (IST)", value: (d) => sheetDateTime(d.reviewed_at) },
  { header: "Rejection reason", value: (d) => d.reject_reason },
  { header: "Purged (IST)", value: (d) => sheetDateTime(d.completed_at) },
];

export function DeletionReview() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("pending");
  const { data, isError, error } = useQuery({
    queryKey: ["admin-deletions", filter],
    queryFn: () => adminListDeletions(filter || undefined),
  });
  // The request whose reject form is open.
  const [rejecting, setRejecting] = useState<string | null>(null);
  // The request whose approval (the purge) is being confirmed.
  const [purging, setPurging] = useState<AdminDeletionRequest | null>(null);

  const review = useMutation({
    mutationFn: ({ id, approve, note }: Review) => adminReviewDeletion(id, approve, note),
    onSuccess: (r) => {
      setRejecting(null);
      if (r.status === "failed") toast.error("Approved, but the purge failed. It's listed under Failed.");
      else toast.success(OUTCOME[r.status] ?? "Request reviewed.");
      qc.invalidateQueries({ queryKey: ["admin-deletions"] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "DELETION_REQUEST_PENDING_LEGAL_HOLD") {
        toast.error("Can't approve yet: the account has data under legal hold.");
        return;
      }
      toast.error(describeError(e));
      // Someone else decided it first: show where it stands now.
      if (e instanceof ApiError && e.code === "ALREADY_REVIEWED") qc.invalidateQueries({ queryKey: ["admin-deletions"] });
    },
    onSettled: () => setPurging(null),
  });
  const reviewing = (d: AdminDeletionRequest, approve: boolean) =>
    review.isPending && review.variables?.id === d.id && review.variables.approve === approve;

  const what = (d: AdminDeletionRequest) =>
    d.scope === "account" ? "the account" : `the ${scopeLabel(d.scope).toLowerCase()} data`;

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Deletion requests</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={filter}
            options={FILTERS.map(([value, label]) => ({ value, label }))}
            onChange={setFilter}
            aria-label="Show requests"
            className="text-sm"
          />
          {/* Every request with the status shown (the API returns them all). */}
          <ExportButton table="deletion requests" columns={EXPORT_COLUMNS} rows={data ?? []} disabled={!data} />
        </div>
      </div>
      {isError && <p className="text-sm text-red-600">{describeError(error)}</p>}

      <ul className="space-y-2">
        {(data ?? []).map((d) => (
          <li key={d.id} className="rounded-xl border bg-white p-4 shadow-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{scopeLabel(d.scope)}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs capitalize ${STATUS_TONE[d.status] ?? "bg-gray-100 text-gray-600"}`}>
                {d.status}
              </span>
            </div>
            <div className="mt-1 text-sm text-gray-800">
              Requested by <strong>{d.requester_name ?? "Unknown"}</strong>
              {d.requester_phone_masked && (
                <span className="whitespace-nowrap text-gray-500"> · {phoneLabel(d.requester_phone_masked)}</span>
              )}
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600">{d.reason}</p>
            <div className="text-xs text-gray-400">{new Date(d.created_at).toLocaleString()}</div>
            {d.reviewed_at && (
              <div className="mt-1 text-xs text-gray-500">
                Reviewed{d.reviewed_by_name ? ` by ${d.reviewed_by_name}` : ""} · {new Date(d.reviewed_at).toLocaleString()}
              </div>
            )}
            {d.reject_reason && <p className="mt-1 break-words text-sm text-red-700">Reason: {d.reject_reason}</p>}

            {d.status === "pending" &&
              (rejecting === d.id ? (
                <RejectForm
                  pending={review.isPending}
                  onSubmit={(note) => review.mutate({ id: d.id, approve: false, note })}
                  onCancel={() => setRejecting(null)}
                />
              ) : (
                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                  <button
                    type="button"
                    onClick={() => setPurging(d)}
                    disabled={review.isPending}
                    className="rounded-lg border px-3 py-1.5 text-emerald-700 disabled:opacity-50"
                  >
                    {reviewing(d, true) ? "Approving…" : "Approve & purge"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRejecting(d.id)}
                    disabled={review.isPending}
                    className="rounded-lg border px-3 py-1.5 text-red-600 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              ))}
          </li>
        ))}
        {data && data.length === 0 && <p className="text-sm text-gray-400">No requests.</p>}
      </ul>

      <ConfirmDelete
        open={purging !== null}
        title="Approve and purge?"
        confirmLabel="Approve & purge"
        busyLabel="Approving…"
        busy={review.isPending}
        onConfirm={() => purging && review.mutate({ id: purging.id, approve: true })}
        onClose={() => setPurging(null)}
      >
        {purging && (
          <p>
            This purges {what(purging)} of <b className="text-gray-800">{requester(purging)}</b>. It can't be undone.
          </p>
        )}
      </ConfirmDelete>
    </div>
  );
}

/** The reason for rejecting: required, since the requester is shown it. */
function RejectForm({
  pending,
  onSubmit,
  onCancel,
}: {
  pending: boolean;
  onSubmit: (note: string) => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState("");
  const reason = note.trim();
  return (
    <form
      className="mt-3 space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (reason && !pending) onSubmit(reason);
      }}
    >
      <label className="block text-sm">
        <span className="text-gray-700">Why is it rejected? The requester sees this.</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={NOTE_MAX}
          rows={3}
          autoFocus
          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
        />
      </label>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button type="submit" disabled={!reason || pending} className="rounded-lg bg-red-600 px-3 py-1.5 text-white disabled:opacity-50">
          {pending ? "Rejecting…" : "Reject request"}
        </button>
        <button type="button" onClick={onCancel} disabled={pending} className="rounded-lg border px-3 py-1.5 disabled:opacity-50">
          Cancel
        </button>
        <span className="ml-auto text-xs tabular-nums text-gray-400">
          {note.length}/{NOTE_MAX}
        </span>
      </div>
    </form>
  );
}

function scopeLabel(scope: string): string {
  const words = scope.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "Ravi Kumar (•••••• 3210)" — whose data the purge erases. */
function requester(d: AdminDeletionRequest): string {
  const name = d.requester_name ?? "this requester";
  return d.requester_phone_masked ? `${name} (${phoneLabel(d.requester_phone_masked)})` : name;
}
